import { defineStore } from 'pinia';
import { ref } from 'vue';
import { uuid4 } from '~~/common/utils';

let nextIndexCounter = 0;

type BaseToasterType = {
  title?: string;
  text?: string;
  type?: 'success' | 'successGreen';
  manualClose?: boolean;
  screenReaderOnly?: boolean;
  showModifyButton?: boolean;
  modifyButtonCallback?: () => unknown;
  /**
   * Action buttons keyed by the outcome they stand for; `AwToaster` renders one per key and calls
   * `cb` with that key. Same shape as the notification store's.
   */
  promise?: Record<string, {
    cb?: (outcome: string) => void;
    styling?: string;
    textToken?: string;
    textString?: string;
  }> | null;
  closeAfter?: number;
  callback?: (() => unknown) | null;
};

type ToasterType = BaseToasterType & {
  uuid?: string;
  toasterKey?: number;
};

export const useToasterStore = defineStore('toaster', () => {
  const toasters = ref<Record<number, ToasterType>>({});

  function setToaster (indexCounter: number, toaster: BaseToasterType) {
    const uuid = uuid4();
    toasters.value[indexCounter] = {
      ...toaster,
      uuid,
      toasterKey: indexCounter,
    };
  }

  function push ({
    title,
    text,
    type,
    manualClose,
    screenReaderOnly,
    showModifyButton,
    modifyButtonCallback,
    promise,
    closeAfter,
    callback,
  }: BaseToasterType) {
    setToaster(nextIndexCounter, {
      title,
      text,
      type,
      manualClose,
      screenReaderOnly,
      showModifyButton,
      modifyButtonCallback,
      promise,
      closeAfter,
      callback,
    });
    return nextIndexCounter++;
  }

  async function remove (key: number) {
    if (toasters.value[key]?.callback) {
      await toasters.value[key].callback();
      if (toasters.value[key]) {
        toasters.value[key].callback = null; // To prevent double callback call on toast removal.
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete toasters.value[key];
  }

  return {
    toasters,
    push,
    remove,
  };
});
